
// apps/admin-ui/src/components/RequirePermission.tsx
// Route-level permission guard for admin-ui
// ALL RIGHTS RESERVED
//
// ADDED 2026-02-07: Phase 1 RBAC — gate all routes by permission

import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { getCurrentEmployee } from '../utils/auth';
import { hasPermission, type Permission } from '../utils/rbac';

interface RequirePermissionProps {
  /** One or more permissions — access is granted if the employee has ANY of them */
  permission: Permission | Permission[];
  children: ReactNode;
}

/**
 * Wraps a route element. Renders children if the current employee holds
 * at least one of the required permissions; otherwise shows Access Denied
 * with a link back to the dashboard.
 */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const employee = getCurrentEmployee();

  if (!employee) {
    return <Navigate to="/" replace />;
  }

  const permissions = Array.isArray(permission) ? permission : [permission];
  const allowed = permissions.some(p => hasPermission(employee.roles, p));

  if (!allowed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
        <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold mb-2 text-content">Access Denied</h2>
        <p className="text-content-secondary mb-4">
          Your role does not have permission to view this page.
        </p>
        <a href="/" className="text-brand-500 hover:text-brand-600 text-sm font-medium">
          ← Back to Dashboard
        </a>
      </div>
    );
  }

  return <>{children}</>;
}

export default RequirePermission;
